<template>
  <div :class="cn('j-shuttle', props.class)">
    <div class="j-shuttle-container">
      <!-- 왼쪽 패널 -->
      <div class="j-shuttle-panel">
        <div class="j-shuttle-panel-card">
          <!-- 헤더 -->
          <div class="j-shuttle-panel-header">
            <h4 class="j-shuttle-panel-title">
              {{ leftTitle || 'Unmapped' }}
            </h4>
            <span class="j-shuttle-panel-count">
              {{ filteredLeftData.length }} items
            </span>
          </div>

          <!-- 검색 -->
          <div v-if="searchable" class="j-shuttle-search">
            <JInput
              v-model="leftSearch"
              placeholder="검색..."
            >
              <template #prefix>
                <JIcon name="search" class="w-4 h-4 text-muted-foreground" />
              </template>
            </JInput>
          </div>

          <!-- 그리드 -->
          <div class="j-shuttle-grid-wrapper">
            <JGrid
              ref="leftGridRef"
              v-model:selected-rows="leftSelectedRows"
              :row-data="filteredLeftData"
              :column-defs="columnDefs"
              :checkbox="true"
              :row-numbers="false"
              :pagination="false"
              class="h-full"
            />
          </div>

          <!-- 푸터 (선택 개수) -->
          <div class="j-shuttle-panel-footer">
            <span class="text-xs text-muted-foreground">
              <template v-if="leftSelectedRows.length > 0">
                <strong>{{ leftSelectedRows.length }}</strong> selected
              </template>
              <template v-else>
                No items selected
              </template>
            </span>
          </div>
        </div>
      </div>

      <!-- 중앙 버튼 영역 -->
      <div class="j-shuttle-actions">
        <div class="j-shuttle-actions-divider" />
        <div class="j-shuttle-actions-buttons">
          <JButton
            styletype="primary"
            size="xs"
            :disabled="leftSelectedRows.length === 0"
            class="j-shuttle-action-btn"
            title="선택한 항목을 오른쪽으로 이동"
            @click="moveToRight"
          >
            <JIcon name="chevronRight" class="w-3.5 h-3.5" />
          </JButton>
          <JButton
            variant="outline"
            size="xs"
            :disabled="rightSelectedRows.length === 0"
            class="j-shuttle-action-btn"
            title="선택한 항목을 왼쪽으로 이동"
            @click="moveToLeft"
          >
            <JIcon name="chevronLeft" class="w-3.5 h-3.5" />
          </JButton>
        </div>
      </div>

      <!-- 오른쪽 패널 -->
      <div class="j-shuttle-panel">
        <div class="j-shuttle-panel-card">
          <!-- 헤더 -->
          <div class="j-shuttle-panel-header j-shuttle-panel-header--primary">
            <h4 class="j-shuttle-panel-title">
              {{ rightTitle || 'Mapped' }}
            </h4>
            <span class="j-shuttle-panel-count j-shuttle-panel-count--primary">
              {{ filteredRightData.length }} items
            </span>
          </div>

          <!-- 검색 -->
          <div v-if="searchable" class="j-shuttle-search">
            <JInput
              v-model="rightSearch"
              placeholder="검색..."
            >
              <template #prefix>
                <JIcon name="search" class="w-4 h-4 text-muted-foreground" />
              </template>
            </JInput>
          </div>

          <!-- 그리드 -->
          <div class="j-shuttle-grid-wrapper">
            <JGrid
              ref="rightGridRef"
              v-model:selected-rows="rightSelectedRows"
              :row-data="filteredRightData"
              :column-defs="columnDefs"
              :checkbox="true"
              :row-numbers="false"
              :pagination="false"
              class="h-full"
            />
          </div>

          <!-- 푸터 (선택 개수) -->
          <div class="j-shuttle-panel-footer">
            <span class="text-xs text-muted-foreground">
              <template v-if="rightSelectedRows.length > 0">
                <strong>{{ rightSelectedRows.length }}</strong> selected
              </template>
              <template v-else>
                No items selected
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { JGrid, JButton, JIcon, JInput } from '@/components/atoms'
import { cn } from '@/lib/utils'
import type { JShuttleProps, JShuttleEmits, ShuttleItem } from '@/types/shuttle.types'

const props = withDefaults(defineProps<JShuttleProps>(), {
  searchable: false,
  leftTitle: 'Unmapped',
  rightTitle: 'Mapped',
})

const emit = defineEmits<JShuttleEmits>()

// 선택된 행
const leftSelectedRows = ref<ShuttleItem[]>([])
const rightSelectedRows = ref<ShuttleItem[]>([])

// 검색어
const leftSearch = ref('')
const rightSearch = ref('')

// 그리드 참조
const leftGridRef = ref()
const rightGridRef = ref()

// 검색 필터링된 데이터
const filteredLeftData = computed(() => {
  if (!props.searchable || !leftSearch.value) {
    return props.leftData
  }
  const search = leftSearch.value.toLowerCase()
  return props.leftData.filter((item) => {
    return Object.values(item).some((val) =>
      String(val).toLowerCase().includes(search)
    )
  })
})

const filteredRightData = computed(() => {
  if (!props.searchable || !rightSearch.value) {
    return props.rightData
  }
  const search = rightSearch.value.toLowerCase()
  return props.rightData.filter((item) => {
    return Object.values(item).some((val) =>
      String(val).toLowerCase().includes(search)
    )
  })
})

// 왼쪽 → 오른쪽 이동
function moveToRight() {
  if (leftSelectedRows.value.length === 0) return

  const itemsToMove = [...leftSelectedRows.value]
  const newLeftData = props.leftData.filter(
    (item) => !itemsToMove.some((selected) => selected.id === item.id)
  )
  const newRightData = [...props.rightData, ...itemsToMove]

  emit('update:leftData', newLeftData)
  emit('update:rightData', newRightData)
  emit('move', { items: itemsToMove, direction: 'toRight' })

  leftSelectedRows.value = []
}

// 오른쪽 → 왼쪽 이동
function moveToLeft() {
  if (rightSelectedRows.value.length === 0) return

  const itemsToMove = [...rightSelectedRows.value]
  const newRightData = props.rightData.filter(
    (item) => !itemsToMove.some((selected) => selected.id === item.id)
  )
  const newLeftData = [...props.leftData, ...itemsToMove]

  emit('update:leftData', newLeftData)
  emit('update:rightData', newRightData)
  emit('move', { items: itemsToMove, direction: 'toLeft' })

  rightSelectedRows.value = []
}

defineExpose({
  leftGridRef,
  rightGridRef,
})
</script>

<style scoped>
.j-shuttle {
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.j-shuttle-container {
  display: flex;
  gap: 0.5rem;
  height: 100%;
  padding: 0;
  border-radius: 0;
  background: transparent;
}

.j-shuttle-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.j-shuttle-panel-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: hsl(var(--card));
  border: 1px solid hsl(var(--border));
  border-radius: 0.125rem; /* rounded-sm */
  box-shadow: none;
  overflow: hidden;
}

.j-shuttle-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 1rem;
  border-bottom: 1px solid hsl(var(--border));
  background: hsl(var(--muted) / 0.3);
  transition: background-color 0.2s ease;
}

.j-shuttle-panel-header--primary {
  background: linear-gradient(135deg,
    hsl(var(--primary) / 0.08) 0%,
    hsl(var(--primary) / 0.02) 100%);
}

.j-shuttle-panel-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: hsl(var(--foreground));
  letter-spacing: -0.01em;
}

.j-shuttle-panel-count {
  font-size: 0.75rem;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  padding: 0.25rem 0.625rem;
  border-radius: 0.125rem; /* rounded-sm */
  background: hsl(var(--muted));
  border: 1px solid hsl(var(--border) / 0.5);
}

.j-shuttle-panel-count--primary {
  color: hsl(var(--primary));
  background: hsl(var(--primary) / 0.1);
  border-color: hsl(var(--primary) / 0.2);
}

.j-shuttle-search {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid hsl(var(--border) / 0.5);
  background: hsl(var(--card));
}

.j-shuttle-grid-wrapper {
  flex: 1;
  overflow: hidden;
  padding: 0;
  background: hsl(var(--background));
}

.j-shuttle-panel-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.625rem 1rem;
  border-top: 1px solid hsl(var(--border) / 0.5);
  background: hsl(var(--muted) / 0.2);
  min-height: 2.5rem;
}

.j-shuttle-actions {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-width: 2.5rem;
}

.j-shuttle-actions-divider {
  position: absolute;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom,
    transparent 0%,
    hsl(var(--border)) 10%,
    hsl(var(--border)) 90%,
    transparent 100%);
}

.j-shuttle-actions-buttons {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.375rem;
  background: transparent;
  border: none;
  box-shadow: none;
}

.j-shuttle-action-btn {
  min-width: 2rem;
  min-height: 2rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.j-shuttle-action-btn:not(:disabled):hover {
  transform: scale(1.08);
  box-shadow: 0 2px 6px 0 rgb(0 0 0 / 0.12);
}

.j-shuttle-action-btn:not(:disabled):active {
  transform: scale(1.02);
}

.j-shuttle-action-btn:disabled {
  opacity: 1;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground) / 0.4);
  border-color: hsl(var(--border));
  cursor: not-allowed;
  transform: scale(1);
}

/* 다크모드 조정 */
.dark .j-shuttle-container {
  background: linear-gradient(135deg, 
    hsl(var(--muted) / 0.2) 0%, 
    hsl(var(--muted) / 0.05) 100%);
}

.dark .j-shuttle-panel-card {
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / 0.2);
}

.dark .j-shuttle-panel-card:hover {
  box-shadow: 0 4px 6px 0 rgb(0 0 0 / 0.3);
}
</style>
